import {
  VariantStockDetail,
  LICENSE_LABELS,
  PRODUCT_STATUS_HELP,
  Product,
  ProductLicense,
  ProductStatus,
  type UpdateProductBody,
  canSell,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Field, Seg, Toggle } from '../../components/Form';
import { CustomerSelect } from '../../components/pickers';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { useCategories, useInvalidateProducts } from '../../lib/products-api';
import { PrintForStockDialog } from '../queue/dialogs';

const EditableStatus = ProductStatus.exclude(['archived']);

function useUpdateProduct(id: string) {
  const invalidate = useInvalidateProducts();
  const toast = useToast();
  return useMutation({
    mutationFn: (body: UpdateProductBody) => api.patch(`/products/${id}`, Product, body),
    onSuccess: () => invalidate(),
    onError: (err) => toast(errorMessage(err)),
  });
}

function DataCard({ p }: { p: Product }) {
  const update = useUpdateProduct(p.id);
  const toast = useToast();
  const categories = useCategories();
  const [form, setForm] = useState<null | {
    name: string;
    sku: string;
    category: string;
    description: string;
    notes: string;
    custom: boolean;
    customerId: string;
  }>(null);

  if (!form) {
    return (
      <section className="card col" style={{ gap: 4 }}>
        <div className="row between">
          <h2>Dados do produto</h2>
          <button
            type="button"
            className="btn sm"
            onClick={() =>
              setForm({
                name: p.name,
                sku: p.sku,
                category: p.category ?? '',
                description: p.description ?? '',
                notes: p.internalNotes ?? '',
                custom: p.custom,
                customerId: p.customer?.id ?? '',
              })
            }
          >
            Editar
          </button>
        </div>
        <div className="kv">
          <span className="muted">Nome</span>
          <span className="v">{p.name}</span>
        </div>
        <div className="kv">
          <span className="muted">SKU interno</span>
          <span className="v tab">{p.sku}</span>
        </div>
        <div className="kv">
          <span className="muted">Categoria</span>
          <span className="v">{p.category ?? '—'}</span>
        </div>
        <div className="kv">
          <span className="muted">Sob encomenda</span>
          <span className="v">
            {p.custom ? (p.customer ? `Sim · ${p.customer.name}` : 'Sim') : 'Não'}
          </span>
        </div>
        <div
          className="col"
          style={{ gap: 4, padding: '10px 0', borderBottom: '1px solid var(--line)' }}
        >
          <span className="muted sm">Descrição</span>
          <p style={{ whiteSpace: 'pre-line' }}>{p.description ?? '—'}</p>
        </div>
        <div className="col" style={{ gap: 4, padding: '10px 0' }}>
          <span className="muted sm">Notas internas (só a equipe vê)</span>
          <p style={{ whiteSpace: 'pre-line' }}>{p.internalNotes ?? '—'}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="card col" style={{ gap: 12 }}>
      <h2>Dados do produto</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <Field id="pd-name" label="Nome">
          <input
            id="pd-name"
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field id="pd-sku" label="SKU interno">
          <input
            id="pd-sku"
            className="input tab"
            value={form.sku}
            onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })}
          />
        </Field>
      </div>
      <Field id="pd-cat" label="Categoria">
        <input
          id="pd-cat"
          className="input"
          list="pd-cats"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
        />
        <datalist id="pd-cats">
          {(categories.data?.data ?? []).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </datalist>
      </Field>
      <div className="row between" style={{ gap: 12 }}>
        <span>
          Sob encomenda <span className="xs muted">peça única para um cliente</span>
        </span>
        <Toggle
          label="Sob encomenda"
          on={form.custom}
          onChange={(v) => setForm({ ...form, custom: v })}
        />
      </div>
      {form.custom ? (
        <Field id="pd-cust" label="Cliente">
          <CustomerSelect
            id="pd-cust"
            value={form.customerId}
            onChange={(v) => setForm({ ...form, customerId: v })}
          />
        </Field>
      ) : null}
      <Field id="pd-desc" label="Descrição" help="Base para os títulos e descrições dos anúncios.">
        <textarea
          id="pd-desc"
          className="input"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </Field>
      <Field id="pd-notes" label="Notas internas (só a equipe vê)">
        <textarea
          id="pd-notes"
          className="input"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </Field>
      <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={() => setForm(null)}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn p"
          disabled={update.isPending}
          onClick={() => {
            if (!form.name.trim()) {
              toast('O nome não pode ficar vazio');
              return;
            }
            update.mutate(
              {
                name: form.name.trim(),
                ...(form.sku.trim() && form.sku.trim() !== p.sku ? { sku: form.sku.trim() } : {}),
                category: form.category.trim() || null,
                description: form.description.trim() || null,
                internalNotes: form.notes.trim() || null,
                custom: form.custom,
                customerId: form.custom ? form.customerId || null : null,
              },
              {
                onSuccess: () => {
                  setForm(null);
                  toast('Dados do produto salvos');
                },
              },
            );
          }}
        >
          Salvar
        </button>
      </div>
    </section>
  );
}

function TagsCard({ p }: { p: Product }) {
  const update = useUpdateProduct(p.id);
  const toast = useToast();
  const [tag, setTag] = useState('');
  const add = () => {
    const t = tag.trim().toLowerCase();
    if (!t) return;
    if (p.tags.includes(t)) {
      toast(`A tag “${t}” já existe`);
      return;
    }
    update.mutate({ tags: [...p.tags, t] }, { onSuccess: () => setTag('') });
  };
  return (
    <section className="card col" style={{ gap: 10 }}>
      <div className="row between">
        <h2>Tags</h2>
        <span className="xs muted">ajudam na busca e na Biblioteca de mídia</span>
      </div>
      <div className="row wrap" style={{ gap: 8 }}>
        {p.tags.length === 0 ? <p className="sm muted">Nenhuma tag.</p> : null}
        {p.tags.map((t) => (
          <span key={t} className="tchip">
            {t}
            <button
              type="button"
              aria-label={`Remover tag ${t}`}
              onClick={() => update.mutate({ tags: p.tags.filter((x) => x !== t) })}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <input
          className="input"
          aria-label="Nova tag"
          placeholder="nova tag"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
        />
        <button type="button" className="btn" onClick={add}>
          Adicionar
        </button>
      </div>
    </section>
  );
}

function OriginCard({ p }: { p: Product }) {
  const update = useUpdateProduct(p.id);
  const [origin, setOrigin] = useState(p.origin);
  const [url, setUrl] = useState(p.sourceUrl ?? '');
  const [license, setLicense] = useState<ProductLicense>(p.license ?? 'unknown');
  const dirty =
    origin !== p.origin ||
    url !== (p.sourceUrl ?? '') ||
    (origin === 'third_party' && license !== (p.license ?? 'unknown'));
  const sellable = canSell({ origin, license });
  return (
    <section className="card col" style={{ gap: 10 }}>
      <div className="row between">
        <h2>Origem e licença</h2>
        <span className={sellable ? 'badge ok' : 'badge err'}>
          {sellable ? 'Pode vender' : 'Bloqueado para venda'}
        </span>
      </div>
      <Seg
        label="Origem"
        value={origin}
        options={[
          { value: 'own', label: 'Projeto próprio' },
          { value: 'third_party', label: 'De terceiro' },
        ]}
        onChange={setOrigin}
      />
      {origin === 'third_party' ? (
        <>
          <Field id="or-link" label="Link do modelo">
            <input
              id="or-link"
              className="input"
              placeholder="https://… (MakerWorld, Printables, Thingiverse)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </Field>
          <Field id="or-lic" label="Licença">
            <select
              id="or-lic"
              className="input"
              value={license}
              onChange={(e) => setLicense(ProductLicense.parse(e.target.value))}
            >
              {ProductLicense.options.map((l) => (
                <option key={l} value={l}>
                  {LICENSE_LABELS[l]}
                </option>
              ))}
            </select>
          </Field>
        </>
      ) : null}
      <p className="xs muted">
        Projetos de terceiros pedem link e licença comercial antes de vender.
      </p>
      {dirty ? (
        <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn sm p"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({
                origin,
                sourceUrl: origin === 'third_party' ? url.trim() || null : null,
                license: origin === 'third_party' ? license : null,
              })
            }
          >
            Salvar origem
          </button>
        </div>
      ) : null}
    </section>
  );
}

function StatusCard({ p }: { p: Product }) {
  const update = useUpdateProduct(p.id);
  const toast = useToast();
  if (p.status === 'archived') {
    return (
      <section className="card col" style={{ gap: 8 }}>
        <h2>Situação</h2>
        <p className="sm">Arquivado. Use “Desarquivar” no topo para voltar ao catálogo.</p>
      </section>
    );
  }
  return (
    <section className="card col" style={{ gap: 8 }}>
      <h2>Situação</h2>
      <Field id="st" label="Situação do produto" help={PRODUCT_STATUS_HELP[p.status]}>
        <select
          id="st"
          className="input"
          value={p.status}
          onChange={(e) => {
            const status = EditableStatus.parse(e.target.value);
            update.mutate(
              { status },
              {
                onSuccess: () =>
                  toast(
                    `Situação: ${status === 'idea' ? 'Ideia' : status === 'prototype' ? 'Protótipo' : 'Ativo'}`,
                  ),
              },
            );
          }}
        >
          <option value="idea">Ideia</option>
          <option value="prototype">Protótipo</option>
          <option value="active" disabled={!p.canSell}>
            Ativo
          </option>
        </select>
      </Field>
    </section>
  );
}

/** "Pronta entrega" per variant (board Produto-Visao, overview). */
function StockCard({ p }: { p: Product }) {
  const toast = useToast();
  const invalidate = useInvalidateProducts();
  const [printing, setPrinting] = useState<string | null>(null);
  const adjust = useMutation({
    mutationFn: (v: { id: string; delta: number }) =>
      api.post(`/variants/${v.id}/stock/adjust`, VariantStockDetail, { delta: v.delta }),
    onSuccess: (s, v) => {
      invalidate();
      const name = p.variants.find((x) => x.id === v.id)?.name ?? '';
      toast(`${name}: ${s.onHand} em estoque`);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const total = p.variants.reduce((n, v) => n + v.stock.onHand, 0);
  const reserved = p.variants.reduce((n, v) => n + v.stock.reserved, 0);
  return (
    <section className="card col" style={{ gap: 8 }}>
      <div className="row between">
        <h2>Pronta entrega</h2>
        <span className="sm muted">
          {total} em estoque
          {reserved ? ` · ${reserved} ${reserved === 1 ? 'reservada' : 'reservadas'}` : ''}
        </span>
      </div>
      {p.variants.length === 0 ? (
        <p className="sm muted">Sem variações, sem estoque. Cadastre uma variação primeiro.</p>
      ) : (
        <div className="gt">
          <div className="gtr head" style={{ gridTemplateColumns: '1.6fr 60px 70px 70px 96px' }}>
            <span>Variação</span>
            <span className="num">Estoque</span>
            <span className="num">Reserv.</span>
            <span className="num">Disp.</span>
            <span />
          </div>
          {p.variants.map((v) => (
            <div
              key={v.id}
              className="gtr"
              style={{ gridTemplateColumns: '1.6fr 60px 70px 70px 96px' }}
            >
              <span style={{ minWidth: 0 }}>
                <span className="trunc" style={{ display: 'block' }}>
                  {v.name}
                </span>
                <span className="xs muted">{v.sku}</span>
              </span>
              <span className="num">{v.stock.onHand}</span>
              <span className="num">{v.stock.reserved}</span>
              <span className="num">{v.stock.available}</span>
              <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn sm icon"
                  aria-label={`Tirar uma de ${v.name}`}
                  disabled={adjust.isPending}
                  onClick={() =>
                    v.stock.onHand <= v.stock.reserved
                      ? toast(
                          v.stock.reserved
                            ? `Não dá para tirar: ${v.stock.reserved} ${v.stock.reserved === 1 ? 'reservada' : 'reservadas'} em pedido`
                            : 'Não há peças em estoque',
                        )
                      : adjust.mutate({ id: v.id, delta: -1 })
                  }
                >
                  −
                </button>
                <button
                  type="button"
                  className="btn sm icon"
                  aria-label={`Somar uma de ${v.name}`}
                  disabled={adjust.isPending}
                  onClick={() => adjust.mutate({ id: v.id, delta: 1 })}
                >
                  +
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
      <p className="xs muted">
        Peças prontas entram aqui quando você conclui uma impressão para o estoque.
      </p>
      {p.variants.length ? (
        <button
          type="button"
          className="btn sm"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setPrinting(p.variants[0]!.id)}
        >
          Imprimir para estoque
        </button>
      ) : null}
      {printing ? (
        <PrintForStockDialog variantId={printing} onClose={() => setPrinting(null)} />
      ) : null}
    </section>
  );
}

/** "Visão geral" tab (board Produto-Visao). */
export function OverviewTab({ product }: { product: Product }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
      <div className="col" style={{ flex: '3 1 460px', minWidth: 0, gap: 18 }}>
        <DataCard key={product.updatedAt} p={product} />
        <TagsCard p={product} />
        <OriginCard key={`${product.origin}-${product.license}-${product.sourceUrl}`} p={product} />
      </div>
      <aside className="col" style={{ flex: '2 1 300px', minWidth: 0, gap: 18 }}>
        <StatusCard p={product} />
        <StockCard p={product} />
      </aside>
    </div>
  );
}
